// PZ PDP DLC — поведение блока «Add-ons» десктопной страницы товара
// (дизайн-хендоф design_handoff_pdp_dlc).
//
// Что здесь есть: фильтр по типу без перезагрузки, ступенчатое
// раскрытие списка и подпись «In cart» у уже добавленных дополнений.
//
// Чего здесь НЕТ: добавления в корзину. Кладёт стоковый
// product-form-component (см. snippets/pz-pdp-dlc-row.liquid) — свой
// fetch /cart/add.js убил бы контракт CartAddEvent: бейдж корзины,
// cart-drawer, аналитику и live-region для скринридеров. Мы только
// слушаем результат.

// имя события берём из контракта темы, а не строкой: так правка
// assets/events.js не оставит блок слушать несуществующее событие
import { ThemeEvents } from '@theme/events';

const SECTION = '[data-pz-dlc]';
const ROW = '[data-pz-dlc-row]';
const CHIP = '[data-pz-dlc-chip]';
const ALL = 'all';
const FALLBACK_COLLAPSED = 6;
const FALLBACK_EXPANDED = 12;

// Токены-заглушки в переводимых строках: числа после фильтра считает
// браузер, а текст обязан прийти из locales/*.json (см. data-template).
const fillTemplate = (template, values) =>
  Object.entries(values).reduce((text, [token, value]) => text.split(token).join(value), template || '');

// Подпись стоковой кнопки лежит в двух вложенных span внутри
// .add-to-cart-text__content (разметка snippets/add-to-cart-button.liquid,
// вложенность нужна её анимации). Контракта на неё нет, поэтому падаем
// на сам контейнер: подпись хуже анимируется, но не теряется.
const labelOf = (button) =>
  button.querySelector('.add-to-cart-text__content span span') ||
  button.querySelector('.add-to-cart-text__content');

const setInCart = (row, isInCart) => {
  const button = row.querySelector('.pz-dlc__btn');
  const label = button && labelOf(button);
  if (!button || !label) return;

  button.classList.toggle('is-in-cart', isInCart);
  const text = isInCart ? row.dataset.labelInCart : row.dataset.labelAdd;
  if (text) label.textContent = text;
};

const setup = (root) => {
  // Повторный setup той же секции (Theme Editor перерисовывает её на
  // каждую правку) обязан отцепить прошлые слушатели: иначе они копятся
  // поверх уже выброшенных строк. Приём — из стоковой темы.
  root.pzDlcAbort?.abort();
  const abort = new AbortController();
  root.pzDlcAbort = abort;

  const rows = [...root.querySelectorAll(ROW)];
  const chips = [...root.querySelectorAll(CHIP)];
  const subtitle = root.querySelector('[data-pz-dlc-subtitle]');
  const showing = root.querySelector('[data-pz-dlc-showing]');
  const moreButton = root.querySelector('[data-pz-dlc-more]');
  const moreLabel = root.querySelector('[data-pz-dlc-more-label]');

  // Числа приходят из настроек секции data-атрибутами; здесь только
  // страховка на случай, если атрибут не отрендерился — тогда список не
  // должен схлопнуться в ноль строк.
  const collapsed = Number(root.dataset.collapsed) || FALLBACK_COLLAPSED;
  const expanded = Number(root.dataset.expanded) || FALLBACK_EXPANDED;

  // Ступени раскрытия: 6 → 12 (хендоф) → весь список. Третья ступень —
  // наше добавление: коллекции DLC в магазине нет, а у 23 игр из 352
  // дополнений больше 12 (замер 2026-07-29), и без неё хвост списка
  // остался бы недостижимым. Set убирает совпавшие ступени: при настройке
  // мерчанта expanded <= collapsed средняя ступень равна первой, и клик по
  // «Показать все» не менял бы на экране ничего.
  const steps = [...new Set([collapsed, Math.max(collapsed, expanded)])].concat(Infinity);
  let stepIndex = 0;
  let filter = ALL;

  const matchingRows = () => rows.filter((row) => filter === ALL || row.dataset.type === filter);

  const render = () => {
    const matching = matchingRows();
    const cap = Math.min(steps[stepIndex], matching.length);
    const visible = matching.slice(0, cap);

    rows.forEach((row) => {
      row.hidden = true;
      row.classList.remove('is-last');
    });
    visible.forEach((row) => {
      row.hidden = false;
    });
    visible.at(-1)?.classList.add('is-last');

    // Подпись в шапке считает ФИЛЬТР («5 из 12 дополнений»), а подпись в
    // подвале — видимый срез («Showing 1–6 of 12»). Так у двух счётчиков
    // разный смысл; это поведение прототипа PDP DLC Desktop.dc.html.
    if (subtitle) {
      subtitle.textContent = fillTemplate(subtitle.dataset.template, {
        __SHOWN__: matching.length,
        __TOTAL__: rows.length,
      });
    }
    if (showing) {
      showing.textContent = fillTemplate(showing.dataset.template, {
        __SHOWN__: visible.length,
        __TOTAL__: matching.length,
      });
    }
    if (moreButton && moreLabel) {
      const isFullyExpanded = visible.length >= matching.length;
      moreButton.hidden = matching.length <= collapsed;
      moreButton.classList.toggle('is-open', isFullyExpanded);
      // Кнопка обещает ровно то, что сделает следующий клик. Числа в двух
      // подписях считаются по-разному, и это не описка: «Показать все N» —
      // сколько строк будет ВСЕГО, «Показать ещё N» — сколько ДОБАВИТСЯ.
      const nextCap = Math.min(steps[Math.min(stepIndex + 1, steps.length - 1)], matching.length);
      const isLastStep = nextCap >= matching.length;
      const template = isLastStep ? moreButton.dataset.labelMore : moreButton.dataset.labelStep;
      const count = isLastStep ? nextCap : nextCap - visible.length;

      moreLabel.textContent = isFullyExpanded
        ? moreButton.dataset.labelLess
        : fillTemplate(template || moreButton.dataset.labelMore, { __COUNT__: count });
    }
  };

  chips.forEach((chip) => {
    chip.addEventListener('click', () => {
      filter = chip.dataset.pzDlcChip;
      stepIndex = 0;
      chips.forEach((other) => {
        const isActive = other === chip;
        other.classList.toggle('is-active', isActive);
        other.setAttribute('aria-pressed', String(isActive));
      });
      render();
    }, { signal: abort.signal });
  });

  moreButton?.addEventListener('click', () => {
    const matchingCount = matchingRows().length;
    const isFullyExpanded = Math.min(steps[stepIndex], matchingCount) >= matchingCount;
    stepIndex = isFullyExpanded ? 0 : Math.min(stepIndex + 1, steps.length - 1);
    render();
  }, { signal: abort.signal });

  // Событие корзины шлют и product-form-component, и корзина
  // (assets/events.js). В detail.resource лежит СВЕЖАЯ корзина —
  // пересобираем состояние всех строк по её items: тогда подпись
  // «In cart» не только появляется при добавлении, но и снимается,
  // когда покупатель удалил дополнение в cart-drawer. Часть путей
  // product-form.js шлёт пустой resource — там остаётся вариант из
  // detail.data. Начальное состояние не читаем: запрос /cart.js ради
  // подписи на каждой карточке товара дороже, чем сама подпись.
  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    const { resource, data } = event.detail ?? {};
    if (data?.didError) return;

    const items = resource?.items;
    if (Array.isArray(items)) {
      const variantsInCart = new Set(items.map((item) => String(item.variant_id)));
      rows.forEach((row) => setInCart(row, variantsInCart.has(row.dataset.variant)));
      return;
    }

    const row = rows.find((item) => item.dataset.variant === String(data?.variantId));
    if (row) setInCart(row, true);
  }, { signal: abort.signal });

  render();
};

document.querySelectorAll(SECTION).forEach(setup);

// Theme Editor перерисовывает секцию через Section Rendering API, и этот
// модуль повторно не выполняется — без переинициализации фильтр и
// «Показать все» отваливались бы в живом превью до перезагрузки.
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll?.(SECTION).forEach(setup);
});
